// "What can I do here?" — `GET /v1/me/access` (spec/07 §6.3, §7; spec/08 §2.7, §4.2; F-IAM-11).
//
//   GET /v1/me/access                          at platform scope: the platform role's `hq:*` bundle
//   GET /v1/me/access?organizationId=…         in one organisation: the member's role and its set
//   GET /v1/me/access?venueId=…                at one venue: the same, once the venue is theirs
//
// "The same function backs every check (one source of truth for 'what can I do' and 'may I do
// this')" (spec/07 §6.3). So the permission set here is computed by exactly the call the server's
// permission guard makes — `permissionsIn` for an organisation, `permissionsForPlatformRole` at
// platform scope — and a console that shows a button because this lists its permission is shown it
// by the same answer that lets the request through (spec/13 §1: "every screen, button and field is
// shown or hidden from `GET /v1/me/access`; the server checks again").
//
// It reads the live session, past the cookie cache: while a role is being taken away, a stale "yes"
// here is the one answer that must not be given. Consoles refetch it when `access.changed` arrives on
// `member:{id}` (spec/09 §6).
//
// A venue the caller does not work at, or an organisation they are not a member of, is `404` — what
// every organisation route answers them (spec/07 §6.1, spec/08 §2.6).
//
// Before any of that, a console or the staff app has to know where the person works at all
// (spec/13 §1: "then choose a venue if the member has several"; spec/15 §3.1, F-MOB-S-01):
//
//   GET /v1/me/organizations                   the organisations they are an active member of
//   GET /v1/me/venues[?organizationId=…]       the live venues they work at, in those organisations
//
// Both answer from the same facts every organisation route is decided by: the memberships the
// principal is resolved with — active only, so a suspended or removed member sees nothing of that
// organisation, on their very next request — and the member's venue scope, the check the scope
// middleware makes of every venue a request names (spec/07 §6.1). An archived venue is in no list of
// live venues, so it is in neither. Neither needs a permission: where you work is yours to know, and
// what you may do there is `GET /v1/me/access`.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { MemberIdSchema, OrganizationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { CurrencyCodeSchema, PermissionSchema, TimezoneSchema } from "../primitives/scalars.js";
import { RoleLimitsSchema, RoleNameSchema } from "./members.js";
import { VenueSlugSchema, VenueStatusSchema } from "./venues.js";
export const ACCESS_SCOPES = ['platform', 'organization', 'venue'];
export const AccessScopeSchema = z.enum(ACCESS_SCOPES).openapi('AccessScope');
export const MyAccessQuerySchema = z
    .object({
    /** An organisation the caller is a member of. */
    organizationId: OrganizationIdSchema.optional(),
    /** A venue the caller works at. Its organisation is found from it when `organizationId` is absent. */
    venueId: VenueIdSchema.optional(),
})
    .openapi('MyAccessQuery');
export const AccessSecondFactorSchema = z
    .object({ required: z.boolean(), satisfied: z.boolean() })
    .openapi('AccessSecondFactor', { description: 'Whether the console needs a second factor here, and whether this session has one.' });
/**
 * Which Clavian products the organisation has (ADR-0065 decision 2): *stations* — Clavian Stations (every organisation
 * until plans are enforced, F-HQ-07) — and *boot* — Clavian Boot (the `boot` add-on). The console shows the parts of a
 * screen for the products a venue has; the server checks each request again.
 */
export const AccessProductsSchema = z
    .object({ stations: z.boolean(), boot: z.boolean() })
    .openapi('AccessProducts', { description: 'Which Clavian products the organisation has: Clavian Stations and Clavian Boot.' });
export const MyAccessSchema = z
    .object({
    scope: AccessScopeSchema,
    /** `null` at platform scope. */
    organizationId: OrganizationIdSchema.nullable(),
    /** `null` unless a venue was asked about. */
    venueId: VenueIdSchema.nullable(),
    /** The membership (`member:{id}` is the channel `access.changed` arrives on). `null` at platform scope. */
    memberId: MemberIdSchema.nullable(),
    /**
     * The organisation role — built-in or custom — or, at platform scope, the platform role. `null`
     * for a signed-in person with no platform role asking at platform scope.
     */
    role: z.string().nullable(),
    /** The effective permission set, sorted: exactly what the server's permission checks allow. */
    permissions: z.array(PermissionSchema),
    /**
     * The limits the role carries (spec/07 §6.4). The console shows the lower of each and its setting
     * (`effectiveRoleLimit`); the server enforces the same. `null` at platform scope.
     */
    limits: RoleLimitsSchema.nullable(),
    /**
     * Whether this role needs a second factor for the console (spec/07 §6.6) and whether this session
     * has one. A console route answers `second-factor-required` while the first is true and the
     * second false.
     */
    secondFactor: AccessSecondFactorSchema,
    /**
     * Which products the organisation has (ADR-0065), resolved in one place on the server. Present at organisation and
     * venue scope; absent at platform scope. Optional, an addition.
     */
    products: AccessProductsSchema.optional(),
})
    .openapi('MyAccess', {
    description: 'What the signed-in person may do here: the effective permission set and the limits, from the function ' +
        'every server-side check uses (spec/07 §6.3).',
});
export const getMyAccessRoute = createRoute({
    method: 'get',
    path: '/v1/me/access',
    operationId: 'getMyAccess',
    tags: ['me'],
    summary: 'What you may do: in an organisation, at a venue, or on the platform',
    description: 'Needs a signed-in user; read from the live session, never the cookie cache. With `organizationId` or ' +
        '`venueId`, the member’s role, its effective permissions and its limits there; with neither, the platform ' +
        'role’s. A venue you do not work at, or an organisation you are not a member of, is `404`. On a shared ' +
        'counter device it is the PIN holder’s, and `device-locked` while nobody holds the PIN.',
    request: { query: MyAccessQuerySchema },
    responses: {
        200: jsonResponse(MyAccessSchema, 'The effective access.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('A shared counter device nobody has unlocked.', 'device-locked'),
        404: problemResponse('No such organisation or venue, or not one you work at.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const MyOrganizationSchema = z
    .object({
    id: OrganizationIdSchema,
    name: z.string(),
    slug: z.string(),
    /** What its wallets, prices and payouts are in (spec/03 §10). */
    currency: CurrencyCodeSchema,
    /** The membership: `member:{id}` is where `access.changed` arrives. */
    memberId: MemberIdSchema,
    /** The one organisation role held there, built-in or custom (spec/07 §6.1). */
    role: RoleNameSchema,
    /** Works at every venue of the organisation, including ones opened later (spec/07 §6.1). */
    allVenues: z.boolean(),
    /** Whether its console needs a second factor, and whether this session has one (spec/07 §6.6). */
    secondFactor: AccessSecondFactorSchema,
})
    .openapi('MyOrganization', { description: 'An organisation the signed-in person is on the staff of.' });
export const MyOrganizationListSchema = z
    .object({
    /** By name. Empty for someone on no staff: a player, or HQ. */
    data: z.array(MyOrganizationSchema),
})
    .openapi('MyOrganizationList');
export const MyVenueSchema = z
    .object({
    id: VenueIdSchema,
    organizationId: OrganizationIdSchema,
    name: z.string(),
    slug: VenueSlugSchema,
    /** Its time zone decides business days and opening hours (spec/01). */
    timezone: TimezoneSchema,
    currency: CurrencyCodeSchema,
    /** `setup`, `live` or `paused`: an archived (`closed`) venue is never listed. */
    status: VenueStatusSchema,
})
    .openapi('MyVenue', { description: 'A venue the signed-in person works at.' });
export const MyVenueListSchema = z
    .object({
    /** By organisation, as `GET /v1/me/organizations` orders them, then oldest first. */
    data: z.array(MyVenueSchema),
})
    .openapi('MyVenueList');
export const MyVenuesQuerySchema = z
    .object({
    /** One organisation's venues only. One the caller is not on the staff of is `404`. */
    organizationId: OrganizationIdSchema.optional(),
})
    .openapi('MyVenuesQuery');
export const listMyOrganizationsRoute = createRoute({
    method: 'get',
    path: '/v1/me/organizations',
    operationId: 'listMyOrganizations',
    tags: ['me'],
    summary: 'The organisations you are on the staff of',
    description: 'Needs a signed-in user. Active memberships only: a suspended or removed member sees nothing of that ' +
        'organisation. With each, the role held there, whether you work at every venue, and whether its console ' +
        'needs a second factor. On a shared counter device, the PIN holder’s membership of the device’s organisation.',
    responses: {
        200: jsonResponse(MyOrganizationListSchema, 'Your organisations.'),
        401: commonProblemResponses[401],
        403: problemResponse('A shared counter device nobody has unlocked.', 'device-locked'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listMyVenuesRoute = createRoute({
    method: 'get',
    path: '/v1/me/venues',
    operationId: 'listMyVenues',
    tags: ['me'],
    summary: 'The venues you work at',
    description: 'Needs a signed-in user. The live venues of your organisations that your venue scope covers — every venue ' +
        'for a member with no scope, only those venues for one with a scope — and never an archived one. ' +
        '`organizationId` narrows it to one organisation. On a shared counter device, the device’s venue.',
    request: { query: MyVenuesQuerySchema },
    responses: {
        200: jsonResponse(MyVenueListSchema, 'Your venues.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('A shared counter device nobody has unlocked.', 'device-locked'),
        404: problemResponse('No such organisation, or not one you are on the staff of.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const ACCESS_ROUTES = [getMyAccessRoute, listMyOrganizationsRoute, listMyVenuesRoute];
//# sourceMappingURL=access.js.map